import { useAppState, useAppDispatch } from '../../Shared/AppContext';

const DRAFT_VIEW_STATE = "draft";
const PENDING_APPROVAL_VIEW_STATE = "pending_approval";
const APPROVED_VIEW_STATE = "approved";

export const VIEW_STATES = [
  DRAFT_VIEW_STATE,
  PENDING_APPROVAL_VIEW_STATE,
  APPROVED_VIEW_STATE,
];

export function useFeedState() {
  return useAppState();
}

export function useFeedActions() {
  const dispatch = useAppDispatch();

  function initializeState() {
    dispatch((appState) => ({
      ...appState,
      loading: true,
      viewState: APPROVED_VIEW_STATE,
      postDataByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, []])),
      lastPageViewedByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, 0])),
      canViewMorePostsByViewState: Object.fromEntries(VIEW_STATES.map(viewState => [viewState, true])),
      postModalOpen: false,
      commentFormModalOpen: false,
      commentParentId: null,
      commentParentType: null,
      commentModalOpen: false,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  function onResponse(response) {
    dispatch((appState) => {
      const viewState = response.viewState || appState.viewState;

      if (response.feedbackVariant === 'success') {
        const newPostDataByViewState = {
          ...appState.postDataByViewState
        };
        const existingPosts = appState.postDataByViewState?.[viewState] || [];
        // Set for O(1) lookups when deduplicating against existing posts
        // Set.has() is O(1) lookup vs Array.includes() which is O(n), so for a feed with many posts the filtering is faster
        const existingIds = new Set(existingPosts.map(p => p.id));
        const uniqueNewPosts = (response.posts || []).filter(p => !existingIds.has(p.id));
        newPostDataByViewState[viewState] = existingPosts.concat(uniqueNewPosts);

        const newLastPageViewedByViewState = {
          ...appState.lastPageViewedByViewState
        };
        newLastPageViewedByViewState[viewState] = response.pageNumber || (appState.lastPageViewedByViewState?.[viewState] || 0);

        const newCanViewMorePostsByViewState = {
          ...appState.canViewMorePostsByViewState
        };
        newCanViewMorePostsByViewState[viewState] =
          response.canViewMorePosts !== undefined
            ? response.canViewMorePosts
            : (appState.canViewMorePostsByViewState?.[viewState] ?? true);

        return {
          ...appState,
          postDataByViewState: newPostDataByViewState,
          lastPageViewedByViewState: newLastPageViewedByViewState,
          canViewMorePostsByViewState: newCanViewMorePostsByViewState,
          loading: false,
          feedbackMessage: response.feedbackMessage || null,
          feedbackVariant: response.feedbackVariant || null,
        };
      } else {
        return {
          ...appState,
          loading: false,
          feedbackMessage: response.feedbackMessage || null,
          feedbackVariant: response.feedbackVariant || null,
        };
      }
    });
  }

  function onError(feedback) {
    dispatch((appState) => ({
      ...appState,
      loading: false,
      feedbackMessage: feedback.feedbackMessage || null,
      feedbackVariant: feedback.feedbackVariant || null,
    }));
  }

  function setViewState(viewState) {
    dispatch((appState) => ({
      ...appState,
      viewState: viewState,
      loading: true,
    }));
  }

  function movePostViewState(post, targetViewState) {
    dispatch((appState) => {
      const currentViewState = appState.viewState;

      const newPostDataByViewState = {
        ...appState.postDataByViewState
      };
      newPostDataByViewState[currentViewState] =
        (newPostDataByViewState[currentViewState] || []).filter(p => p.id !== post.id);

      post.viewState = targetViewState;
      newPostDataByViewState[targetViewState] = [post].concat(
        (newPostDataByViewState[targetViewState] || []).filter(p => p.id !== post.id)
      );

      return {
        ...appState,
        postDataByViewState: newPostDataByViewState,
      };
    });
  }

  function setPostModalOpen(open) {
    dispatch((appState) => ({
      ...appState,
      postModalOpen: open,
    }));
  }

  function setCommentFormModalOpen(open, commentParentId = null, commentParentType = null) {
    dispatch((appState) => ({
      ...appState,
      commentFormModalOpen: open,
      commentParentId: commentParentId || null,
      commentParentType: commentParentType || null,
    }));
  }

  function setCommentModalOpen(open, commentParentId = null, commentParentType = null) {
    dispatch((appState) => ({
      ...appState,
      commentModalOpen: open,
      commentParentId: commentParentId || null,
      commentParentType: commentParentType || null,
    }));
  }

  function setFeedback(feedbackMessage, feedbackVariant) {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: feedbackMessage,
      feedbackVariant: feedbackVariant,
    }));
  }

  function getFeedbackFromLocalStorage(feedbackMessage, feedbackVariant) {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: feedbackMessage,
      feedbackVariant: feedbackVariant,
    }));
  }

  function clearFeedback() {
    dispatch((appState) => ({
      ...appState,
      feedbackMessage: null,
      feedbackVariant: null,
    }));
  }

  return {
    initializeState,
    onResponse,
    onError,
    setViewState,
    movePostViewState,
    setPostModalOpen,
    setCommentFormModalOpen,
    setCommentModalOpen,
    setFeedback,
    getFeedbackFromLocalStorage,
    clearFeedback,
  };
}
